import { useEffect, useState } from "react";
import { calculatePrayerTimes, PRAYER_LABELS_ID, timeToMinutes, type PrayerKey } from "@/lib/prayer/calc";
import { tzOffsetHours, type Location } from "@/data/locations";
import { displayLocationTitle } from "@/lib/prayer/copy";
import { Clock } from "lucide-react";

const ORDER: PrayerKey[] = ["imsak", "subuh", "terbit", "dhuha", "dzuhur", "ashar", "maghrib", "isya"];
const SHOLAT_ONLY: PrayerKey[] = ["subuh", "dzuhur", "ashar", "maghrib", "isya"];

function fmt2(n: number) { return String(n).padStart(2, "0"); }

interface Props {
  location: Location;
  variant: "sholat" | "imsak";
}

/**
 * Card showing the next upcoming prayer with a JJ:MM:DD countdown.
 * SSR-safe: renders an inert placeholder until mounted on the client.
 */
export function NextPrayerCard({ location, variant }: Props) {
  const [now, setNow] = useState<Date | null>(null);
  const tzHours = tzOffsetHours(location.tz);
  const considered = variant === "sholat" ? SHOLAT_ONLY : ORDER;

  useEffect(() => {
    setNow(new Date());
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Compute current local minutes-of-day in the location's timezone.
  let next: { key: PrayerKey; label: string; diffSec: number; isTomorrow: boolean } | null = null;
  let dateLabel = "—";

  if (now) {
    // Convert "now" to location-local wall clock.
    const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes() + now.getUTCSeconds() / 60;
    const localMinutes = (utcMinutes + tzHours * 60 + 24 * 60) % (24 * 60);
    // Build today's date in location TZ.
    const localDateMs = now.getTime() + tzHours * 3600 * 1000;
    const ld = new Date(localDateMs);
    const y = ld.getUTCFullYear();
    const m = ld.getUTCMonth() + 1;
    const d = ld.getUTCDate();

    const today = calculatePrayerTimes({ y, m, d, lat: location.lat, lng: location.lng, tzHours });
    const tomorrowDate = new Date(Date.UTC(y, m - 1, d + 1));
    const tomorrow = calculatePrayerTimes({
      y: tomorrowDate.getUTCFullYear(),
      m: tomorrowDate.getUTCMonth() + 1,
      d: tomorrowDate.getUTCDate(),
      lat: location.lat, lng: location.lng, tzHours,
    });

    for (const k of considered) {
      const minutes = timeToMinutes(today[k]);
      if (minutes > localMinutes) {
        const diffMin = minutes - localMinutes;
        const diffSec = Math.max(0, Math.round(diffMin * 60 - now.getUTCSeconds()));
        next = { key: k, label: PRAYER_LABELS_ID[k], diffSec, isTomorrow: false };
        break;
      }
    }
    if (!next) {
      const firstKey = considered[0];
      const minutes = timeToMinutes(tomorrow[firstKey]);
      const diffMin = (24 * 60 - localMinutes) + minutes;
      const diffSec = Math.max(0, Math.round(diffMin * 60 - now.getUTCSeconds()));
      next = { key: firstKey, label: PRAYER_LABELS_ID[firstKey], diffSec, isTomorrow: true };
    }

    const days = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
    const months = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
    const dow = ld.getUTCDay();
    dateLabel = `${days[dow]}, ${d} ${months[m - 1]} ${y}`;
  }

  const hh = next ? Math.floor(next.diffSec / 3600) : 0;
  const mm = next ? Math.floor((next.diffSec % 3600) / 60) : 0;
  const ss = next ? next.diffSec % 60 : 0;

  return (
    <section
      aria-label="Sholat Selanjutnya"
      className="mt-4 overflow-hidden rounded-xl bg-gradient-to-br from-prayer to-prayer/80 p-5 text-prayer-foreground shadow-md"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-80">
            Jadwal {variant === "sholat" ? "Sholat" : "Imsakiyah"}
          </p>
          <p className="text-sm font-medium opacity-90">
            {displayLocationTitle(location)} — {dateLabel}
          </p>
        </div>
        <div className="rounded-md bg-white/10 px-3 py-1.5 text-xs font-medium backdrop-blur-sm">
          <Clock className="mr-1 inline h-3 w-3" /> Sholat Selanjutnya
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-display text-4xl font-bold leading-none md:text-5xl">
            {next ? next.label : "—"}
          </p>
          {next?.isTomorrow && (
            <p className="mt-1 text-xs opacity-80">(besok)</p>
          )}
        </div>
        <div className="flex items-center gap-2 font-mono tabular-nums">
          <Slot value={now ? fmt2(hh) : "--"} unit="Jam" />
          <Sep />
          <Slot value={now ? fmt2(mm) : "--"} unit="Menit" />
          <Sep />
          <Slot value={now ? fmt2(ss) : "--"} unit="Detik" />
        </div>
      </div>
    </section>
  );
}

function Slot({ value, unit }: { value: string; unit: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="rounded-md bg-white/15 px-2.5 py-1 text-2xl font-bold md:text-3xl">{value}</span>
      <span className="mt-1 text-[10px] uppercase opacity-80">{unit}</span>
    </div>
  );
}
function Sep() {
  return <span className="self-center pb-4 text-2xl font-bold opacity-60">:</span>;
}
